import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { cn } from '../lib/classnames.js';

interface GildingCeremonyProps {
  /** Tunes the first stage caption: reading a character vs reading a brief. */
  variant?: 'remix' | 'sketch';
  className?: string;
}

/**
 * GildingCeremony — the generation wait staged as the craft itself.
 *
 * Left: a miniature plate being laid with gold leaf. Twenty-five tiles land
 * in scan order (each flashes bright as it catches the light, settles, holds,
 * lifts, relays — a perpetual laying front), under a periodic burnisher sheen
 * and a few motes of drifting gold dust.
 *
 * Middle: stage narration in the craft's voice, keyed so each stage rises in,
 * with an honest elapsed counter.
 *
 * Bottom: the progress rule speaks the site's divider language — a gilded
 * filament extends along a faint hairline road with the faceted lozenge
 * riding its tip. The estimate eases asymptotically toward 92% and holds;
 * it never claims a finish it can't deliver.
 *
 * prefers-reduced-motion: tiles and dust sit static, the sheen is removed,
 * the stage text and filament still advance (width/left transitions are
 * disabled by the global reduced-motion rules, so they simply step).
 */
export function GildingCeremony({ variant = 'sketch', className }: GildingCeremonyProps) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const stage =
    seconds < 6
      ? variant === 'remix'
        ? 'Reading your character'
        : 'Reading the brief'
      : seconds < 14
        ? 'Amplifying with Claude'
        : seconds < 30
          ? 'Laying the gold'
          : seconds < 48
            ? 'Rendering, the long stretch'
            : 'Burnishing the last pass';

  // Honest-feeling estimate: eases toward 92% and holds there until the
  // real response lands.
  const pct = Math.min(92, Math.round(100 * (1 - Math.exp(-seconds / 22))));

  // Deterministic per-tile character: scan-order delay with a touch of
  // jitter, a small tilt, and one of three foil cuts so the plate doesn't
  // read as a uniform swatch. No Math.random — stable across renders.
  const tiles = useMemo(
    () =>
      Array.from({ length: 25 }, (_, i) => ({
        delay: i * 150 + ((i * 7) % 5) * 45,
        tilt: (((i * 13) % 7) - 3) * 1.2,
        cut: TILE_CUTS[(i * 11) % TILE_CUTS.length],
      })),
    [],
  );

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'relative overflow-hidden rounded-sm border border-hairline/60 bg-surface-elevated px-4 pb-3.5 pt-4',
        className,
      )}
    >
      {/* drifting gold dust, behind the content */}
      <span aria-hidden className="gild-dust absolute left-[30%] top-3 h-1 w-1 rounded-full bg-accent-soft" style={{ '--drift-x': '6px', '--drift-dur': '5600ms' } as CSSProperties} />
      <span aria-hidden className="gild-dust absolute left-[62%] top-8 h-[3px] w-[3px] rounded-full bg-accent-soft" style={{ '--drift-x': '-5px', '--drift-dur': '6400ms', '--drift-delay': '900ms' } as CSSProperties} />
      <span aria-hidden className="gild-dust absolute left-[84%] top-4 h-[3px] w-[3px] rounded-full bg-accent-soft" style={{ '--drift-x': '4px', '--drift-dur': '4800ms', '--drift-delay': '2100ms' } as CSSProperties} />

      <div className="flex items-center gap-4">
        {/* ---- The plate being gilded ---- */}
        <div
          aria-hidden
          className="relative h-16 w-16 shrink-0 overflow-hidden rounded-xs border border-hairline-strong/70 bg-surface-sunken p-[3px]"
        >
          <div className="grid h-full w-full grid-cols-5 grid-rows-5 gap-[2px]">
            {tiles.map((t, i) => (
              <span
                key={i}
                className="leaf-tile block"
                style={
                  {
                    '--lay-delay': `${t.delay}ms`,
                    '--tilt': `${t.tilt}deg`,
                    background: t.cut,
                  } as CSSProperties
                }
              />
            ))}
          </div>
          {/* burnisher sheen */}
          <span
            className="plate-sheen pointer-events-none absolute inset-y-0 left-0 w-1/2"
            style={{
              background:
                'linear-gradient(90deg, transparent, rgba(255,243,206,0.32), transparent)',
            }}
          />
        </div>

        {/* ---- Stage narration ---- */}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <p
            key={stage}
            className="truncate font-display text-[0.9rem] font-medium text-ink motion-safe:animate-count-up"
          >
            {stage}…
          </p>
          <p className="font-display italic text-caption text-ink-subtle">
            {seconds}s · thirty to sixty seconds is normal
          </p>
          <p className="font-display italic text-caption text-ink-subtle">
            Keep this page open.
          </p>
        </div>
      </div>

      {/* ---- Filament progress: the divider as a progress rule ---- */}
      <div className="relative mt-3.5 h-3" aria-hidden>
        {/* the road ahead */}
        <span className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-hairline/70" />
        {/* the gilded filament laid so far */}
        <span
          className="absolute left-0 top-1/2 h-[2px] -translate-y-1/2 rounded-full bg-gradient-to-r from-accent-deep via-accent to-accent-soft transition-[width] duration-1000 ease-linear"
          style={{
            width: `${pct}%`,
            boxShadow: '0 0 6px rgb(var(--color-accent) / 0.35)',
          }}
        />
        {/* the faceted lozenge riding the tip */}
        <span
          className="ceremony-tip absolute top-1/2 -translate-x-1/2 -translate-y-1/2 transition-[left] duration-1000 ease-linear"
          style={{ left: `${pct}%` }}
        >
          <svg viewBox="0 0 28 16" className="h-3 w-[1.3rem]">
            <defs>
              <linearGradient id="cerLoz" x1="0" y1="0" x2="0.25" y2="1">
                <stop offset="0" stopColor="#a87f2c" />
                <stop offset="0.42" stopColor="#e9c873" />
                <stop offset="0.52" stopColor="#fff3ce" />
                <stop offset="0.64" stopColor="#c2922f" />
                <stop offset="1" stopColor="#7d521a" />
              </linearGradient>
            </defs>
            <path d="M2 8 L14 3.2 L26 8 L14 12.8 Z" fill="url(#cerLoz)" />
            <path d="M2 8 L26 8" stroke="#fff3ce" strokeWidth="0.5" opacity="0.65" />
            <circle cx="14" cy="8" r="1.1" fill="#fff3ce" />
          </svg>
        </span>
      </div>
    </div>
  );
}

/* Three foil cuts — the same ramp read at different angles, so neighbouring
   tiles catch the light differently the way real laid leaf does. */
const TILE_CUTS = [
  'linear-gradient(135deg, #e9c873 0%, #fff3ce 28%, #c2922f 66%, #7d521a 100%)',
  'linear-gradient(115deg, #d9b25a 0%, #f3dc9a 36%, #a87f2c 74%, #8a6020 100%)',
  'linear-gradient(155deg, #f0d48c 0%, #e9c873 30%, #b98a2e 62%, #6e4814 100%)',
];
